import { action } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type DS from 'ember-data';
import type Roomtype from 'frontend-utils/models/roomtype';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  CHANNEL_ROOM_MAPPING_PRODUCTS,
  CHANNEL_ROOM_MAPPING_ROUTE,
} from 'lumen/utils/central-mapping-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { refreshIfOtaIsDisconnected } from 'lumen/utils/disconnected-otas';

interface RoomtypeResponse extends DS.AdapterPopulatedRecordArray<Roomtype> {
  meta: {
    disconnected_otas: string[];
  };
}

interface ChannelRoomCategoriesRouteModel {
  ratePreferenceTaskResult?: TaskInstance<void>;
  roomtypes: Partial<TaskInstance<Roomtype[]>>;
}

export default class HotelSettingsCentralMappingChannelRoomCategoriesRoute extends Route {
  queryParams = {
    ota: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @service declare router: Services['router'];
  @service declare store: Services['store'];

  modelObject = trackedObject<ChannelRoomCategoriesRouteModel>({
    roomtypes: {
      isError: false,
      isRunning: false,
      value: [] as Roomtype[] | null,
    },
  });

  fetchRoomtypesTask = task(
    { restartable: true },
    async ({ ota }: { ota: string }) => {
      const isAllChannelsSelected = ota === OTA_IDS.ALL_CHANNELS;

      const { hotel } = this.hotelAndMarketService;

      const roomtypes = await this.store.query<Roomtype>('roomtype', {
        namespace: APIGATEWAY,
        hotel_id: hotel.id,
        compset_ids: hotel.sortedCompsets.map(({ apiId }) => apiId).join(','),
        ota,
        typeKey: 'channel_room_categories',
        data_type: isAllChannelsSelected
          ? CHANNEL_ROOM_MAPPING_PRODUCTS.PARITY
          : CHANNEL_ROOM_MAPPING_PRODUCTS.ALL_PRODUCTS,
      });

      // Update disconnected OTAs - refresh when a new ota gets disconnected
      refreshIfOtaIsDisconnected(
        (roomtypes as unknown as RoomtypeResponse).meta.disconnected_otas,
        this.store,
        this.alerting,
      );

      return roomtypes;
    },
  );

  beforeModel(transition: Transition): void {
    super.beforeModel(transition);
    // @ts-expect-error: 'transition.to' is possibly 'undefined'.
    const { ota } = transition.to.queryParams;
    const { OTAsRates } = this.hotelAndMarketService.hotelOrMarket;

    const hasRatesOrPricingManager =
      this.access.hasRates || this.access.hasPricingAssistantTab;

    const validOtas = hasRatesOrPricingManager
      ? OTAsRates.filter((OTARates) => OTARates.isConnectedOrBrandDotCom)
      : [{ id: OTA_IDS.ALL_CHANNELS }];

    if (!validOtas.find((validOta) => validOta.id === ota)) {
      transition.abort();
      const defaultOta =
        validOtas.find((ota) => ota.id === OTA_IDS.BOOKINGDOTCOM) ??
        validOtas[0];
      this.router.transitionTo(
        CHANNEL_ROOM_MAPPING_ROUTE,
        this.hotelAndMarketService.hotel,
        {
          queryParams: { ota: defaultOta?.id },
        },
      );
    }
  }

  model(params: { ota: string }): ChannelRoomCategoriesRouteModel {
    // The influenced products pills compare the selected OTA against the
    // preferred channel, so they show a loading state off this task.
    this.modelObject.ratePreferenceTaskResult =
      this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );

    // Query all roomtypes of all competitors for the roomtype mapping modal
    this.store.unloadAll('roomtype');

    this.modelObject.roomtypes = this.fetchRoomtypesTask.perform({
      ota: params.ota,
    });

    return this.modelObject;
  }

  @action refreshRoute(): void {
    this.refresh();
  }
}
